/** Fit a Visual carousel to the settled slide's intrinsic ratio. Height changes
 * occur after scrolling settles, so mixed photos do not leave a tallest-slide gutter.
 * Metadata/resize updates retain the same track and cleanup releases every observer. */
export function createVisualCarouselHeight(
  track,
  {
    ResizeObserver = globalThis.ResizeObserver,
    delay = globalThis.setTimeout,
    cancelDelay = globalThis.clearTimeout,
  } = {},
) {
  let timer,
    disposed = false;
  const media = [...track.children].map((node) => node.querySelector('img, video'));
  function fit() {
    if (disposed || !track.clientWidth) return;
    const index = Math.max(
      0,
      Math.min(media.length - 1, Math.round(track.scrollLeft / track.clientWidth)),
    );
    const current = media[index];
    const width =
      current?.naturalWidth || current?.videoWidth || Number(current?.getAttribute?.('width'));
    const height =
      current?.naturalHeight || current?.videoHeight || Number(current?.getAttribute?.('height'));
    const ratio =
      width > 0 && height > 0 ? width / height : Number(track.children[index]?.dataset.visualRatio);
    if (!Number.isFinite(ratio) || ratio <= 0) return;
    const value = `${Math.round(track.clientWidth / ratio)}px`;
    if (track.style.height !== value) track.style.height = value;
  }
  function settled() {
    cancelDelay(timer);
    fit();
  }
  function scrolling() {
    cancelDelay(timer);
    timer = delay(settled, 120);
  }
  track.addEventListener('scroll', scrolling, { passive: true });
  track.addEventListener('scrollend', settled);
  for (const item of media) {
    item?.addEventListener('load', fit);
    item?.addEventListener('loadedmetadata', fit);
  }
  const observer = ResizeObserver && new ResizeObserver(fit);
  observer?.observe(track);
  fit();
  return {
    dispose() {
      disposed = true;
      cancelDelay(timer);
      observer?.disconnect();
      track.removeEventListener('scroll', scrolling);
      track.removeEventListener('scrollend', settled);
      for (const item of media) {
        item?.removeEventListener('load', fit);
        item?.removeEventListener('loadedmetadata', fit);
      }
    },
  };
}

/** Coordinate silent inline video playback. Native controls remain the fallback
 * when browser policy rejects play; only one sufficiently visible video starts.
 * Inject browser primitives so visibility, manual pause and recovery can be tested. */
export function createMediaPlayback(
  doc,
  {
    Observer = globalThis.IntersectionObserver,
    motion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)'),
    viewportHeight = () => globalThis.innerHeight,
    viewportWidth = () => globalThis.innerWidth,
  } = {},
) {
  const players = new Map();
  const carousels = new Map();
  let disposed = false;
  function pause(video, state) {
    if (video.paused) return;
    state.automaticPause = true;
    video.pause();
  }
  function update() {
    if (disposed) return;
    for (const [track, controller] of carousels) {
      if (!track.isConnected) {
        controller.dispose();
        carousels.delete(track);
      }
    }
    const eligible = [...players.entries()]
      .filter(
        ([video, state]) =>
          video.isConnected &&
          !doc.hidden &&
          !motion?.matches &&
          !state.manualPause &&
          !state.rejected &&
          state.visible,
      )
      .sort((a, b) => b[1].ratio - a[1].ratio);
    const candidate = eligible[0]?.[0];
    for (const [video, state] of players) {
      if (!video.isConnected) {
        pause(video, state);
        observer?.unobserve(video);
        players.delete(video);
        continue;
      }
      if (doc.hidden || !state.visible || (candidate && video !== candidate)) pause(video, state);
    }
    if (!candidate || !candidate.paused || players.get(candidate).starting) return;
    const state = players.get(candidate);
    // Never silently resume sound after someone unmutes a player.
    if (!candidate.muted) return;
    state.starting = true;
    Promise.resolve()
      .then(() => {
        if (
          disposed ||
          doc.hidden ||
          !candidate.isConnected ||
          !candidate.muted ||
          !candidate.paused ||
          !state.visible ||
          state.manualPause ||
          motion?.matches
        )
          return;
        return candidate.play();
      })
      .catch(() => {
        state.rejected = true;
      })
      .finally(() => {
        state.starting = false;
      });
  }
  const observer =
    Observer &&
    new Observer(
      (entries) => {
        for (const entry of entries) {
          const state = players.get(entry.target);
          if (!state) continue;
          state.ratio = entry.intersectionRatio;
          state.visible =
            entry.isIntersecting &&
            entry.intersectionRect.width >=
              Math.min(entry.boundingClientRect.width, viewportWidth()) * 0.6 &&
            entry.intersectionRect.height >=
              Math.min(entry.boundingClientRect.height, viewportHeight()) * 0.6;
        }
        update();
      },
      { threshold: [0, 0.15, 0.5, 0.75, 1] },
    );
  const visibility = () => update();
  const motionChange = () => {
    if (motion.matches) for (const [video, state] of players) pause(video, state);
    update();
  };
  doc.addEventListener('visibilitychange', visibility);
  motion?.addEventListener('change', motionChange);
  function refresh(root) {
    if (disposed) return;
    for (const track of root.querySelectorAll('.visual-immersive .layout-carousel .media-items')) {
      // The Visual frame owns a stable viewport stage; original-ratio fitting
      // remains available for older/unframed carousel consumers.
      if (track.closest?.('.visual-media-frame')) continue;
      if (!carousels.has(track)) carousels.set(track, createVisualCarouselHeight(track));
    }
    for (const video of root.querySelectorAll('video')) {
      if (players.has(video)) continue;
      const state = {
        visible: false,
        ratio: 0,
        manualPause: false,
        automaticPause: false,
        starting: false,
        rejected: false,
      };
      state.onPause = () => {
        if (state.automaticPause) state.automaticPause = false;
        else if (!video.ended) state.manualPause = true;
      };
      state.onPlay = () => {
        state.manualPause = false;
        state.rejected = false;
        for (const [other, otherState] of players) if (other !== video) pause(other, otherState);
      };
      video.addEventListener('pause', state.onPause);
      video.addEventListener('play', state.onPlay);
      players.set(video, state);
      observer?.observe(video);
    }
    update();
  }
  function dispose() {
    disposed = true;
    observer?.disconnect();
    doc.removeEventListener('visibilitychange', visibility);
    motion?.removeEventListener('change', motionChange);
    for (const [video, state] of players) {
      pause(video, state);
      video.removeEventListener('pause', state.onPause);
      video.removeEventListener('play', state.onPlay);
    }
    players.clear();
    for (const controller of carousels.values()) controller.dispose();
    carousels.clear();
  }
  return { refresh, dispose };
}
